import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type {
  BrandDirectoryListing,
  BrandDirectoryListingFilter,
  BrandDirectoryStateFilterListing,
  BrandDirectoryStateFilterListingQueryParameters,
  BrandDirectoryListingQueryParameters,
  BrandDirectoryModal,
} from "./type";

const initialState: BrandDirectoryModal = {
  brandDirectoryListing: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },
  brandDirectoryListingQueryParameters: {
    pageNo: 1,
    limit: 10,
    searchKey: "",
  },
  brandDirectoryListingFilter: {
    manufacturerState: [],
    sortBy: 1,
    // For this page we always sort by company name (manufacturerCompanyName).
    sortCriteria: "companyName",
  },
  brandDirectoryStateFilterListing: {
    data: [],
    total: 0,
    limit: 0,
    pageNo: 0,
    nextHit: 0,
    totalPage: 0,
  },
  brandDirectoryStateFilterListingQueryParameters: {
    pageNo: 1,
    limit: 10,
    searchKey: "",
  },
  hasMore: false,
  hasMoreStates: false,
};

const brandDirectorySlice = createSlice({
  name: "brandDirectorySlice",
  initialState,
  reducers: {
    setBrandDirectoryListingData: (
      state: BrandDirectoryModal,
      action: PayloadAction<BrandDirectoryListing>,
    ) => {
      const newPayload = action.payload;

      if (newPayload.pageNo > 1) {
        state.brandDirectoryListing = {
          ...newPayload,
          data: [...state.brandDirectoryListing.data, ...newPayload.data],
        };
      } else {
        state.brandDirectoryListing = newPayload;
      }

      state.hasMore = newPayload.totalPage > newPayload.pageNo;
    },

    setBrandDirectoryStateFilterListingData: (
      state: BrandDirectoryModal,
      action: PayloadAction<BrandDirectoryStateFilterListing>,
    ) => {
      const newPayload = action.payload;

      if (newPayload.pageNo > 1) {
        state.brandDirectoryStateFilterListing = {
          ...newPayload,
          data: [
            ...state.brandDirectoryStateFilterListing.data,
            ...newPayload.data,
          ],
        };
      } else {
        state.brandDirectoryStateFilterListing = newPayload;
      }

      state.hasMoreStates = newPayload.totalPage > newPayload.pageNo;
    },

    setBrandDirectoryListingQueryParameters: (
      state: BrandDirectoryModal,
      action: PayloadAction<Partial<BrandDirectoryListingQueryParameters>>,
    ) => {
      state.brandDirectoryListingQueryParameters = {
        ...state.brandDirectoryListingQueryParameters,
        ...action.payload,
      };
    },

    setBrandDirectoryListingFilter: (
      state: BrandDirectoryModal,
      action: PayloadAction<Partial<BrandDirectoryListingFilter>>,
    ) => {
      state.brandDirectoryListingFilter = {
        ...state.brandDirectoryListingFilter,
        ...action.payload,
      };
    },

    setBrandDirectoryStateFilterListingQueryParameters: (
      state: BrandDirectoryModal,
      action: PayloadAction<
        Partial<BrandDirectoryStateFilterListingQueryParameters>
      >,
    ) => {
      state.brandDirectoryStateFilterListingQueryParameters = {
        ...state.brandDirectoryStateFilterListingQueryParameters,
        ...action.payload,
      };
    },

    /**
     * Reset only the listing data for infinite scroll refresh.
     * Keep current filters/sort values, but bring `pageNo` back to 1.
     */
    setResetBrandDirectoryListing: (state: BrandDirectoryModal) => {
      state.brandDirectoryListing = {
        data: [],
        total: 0,
        limit: state.brandDirectoryListingQueryParameters.limit,
        pageNo: 0,
        nextHit: 0,
        totalPage: 0,
      };
      state.brandDirectoryListingQueryParameters.pageNo = 1;
      state.hasMore = false;
    },

    setResetBrandDirectoryStateFilterListing: (state: BrandDirectoryModal) => {
      state.brandDirectoryStateFilterListing = {
        data: [],
        total: 0,
        limit: state.brandDirectoryStateFilterListingQueryParameters.limit,
        pageNo: 0,
        nextHit: 0,
        totalPage: 0,
      };
      state.brandDirectoryStateFilterListingQueryParameters.pageNo = 1;
      state.hasMoreStates = false;
    },

    /** Fully clear page state (filters, sort, and listing). */
    clearBrandDirectoryState: () => initialState,

    setBrandDirectorySortByCriteria: (
      state: BrandDirectoryModal,
      action: PayloadAction<"A-Z" | "Z-A">,
    ) => {
      state.brandDirectoryListingFilter.sortCriteria = "companyName";
      state.brandDirectoryListingFilter.sortBy =
        action.payload === "A-Z" ? 1 : 0;
    },
  },
});

export const {
  setBrandDirectoryListingData,
  setBrandDirectoryStateFilterListingData,
  setBrandDirectoryListingQueryParameters,
  setBrandDirectoryListingFilter,
  setBrandDirectoryStateFilterListingQueryParameters,
  setResetBrandDirectoryListing,
  setResetBrandDirectoryStateFilterListing,
  clearBrandDirectoryState,
  setBrandDirectorySortByCriteria,
} = brandDirectorySlice.actions;

export default brandDirectorySlice.reducer;
